import React from 'react';
import {observer} from 'mobx-react';
import {withState} from 'shared/core';
import {EmployeeSelectorState} from '../state';
import Spinner from 'components/spinners/Spinner';
import LabelHelper from 'components/LabelHelper';
import { ActionLink } from 'components/links';
import { FormLayout } from 'components/forms';
import ObjectSelect2 from 'components/ObjectSelect2';
import RadioButton from 'components/RadioButton';
import RadioButtonGroup from 'components/RadioButtonGroup';
import EmployeeNameWithAvatar from 'components/EmployeeNameWithAvatar';
import {FormattedMessage} from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

const MultiSelector = observer(({uiState, selectors}) => {
  if (uiState.selectionType !== 'select_employees') return null;

  const {
    allEmployees,
    employees,
    locations,
    departments,
    managers,
    uniqueSelectedEmployees,
    errors
  } = uiState;

  return (
    <FormLayout>
      {selectors.includes('location') && <ObjectSelect2
        multiple
        items={locations}
        onAdd={location => uiState.selectLocation(location)}
        label='components.employee_selector.By location:'
        placeholder='components.employee_selector.Add employees by location'
      />}
      {selectors.includes('department') && <ObjectSelect2
        multiple
        items={departments}
        onAdd={department => uiState.selectDepartment(department)}
        label='components.employee_selector.By department:'
        placeholder='components.employee_selector.Add employees by department'
      />}
      {selectors.includes('manager') && <ObjectSelect2
        multiple
        items={managers}
        onAdd={employee => uiState.selectManager(employee)}
        label='components.employee_selector.By manager:'
        placeholder='components.employee_selector.Add employees by manager'
      />}
      {selectors.includes('employee') && <ObjectSelect2
        multiple
        items={employees}
        onAdd={employee => uiState.selectEmployee(employee)}
        label='components.employee_selector.By employee:'
        placeholder='components.employee_selector.Add employees'
        errorMessage={errors.assignees}
      />}
      {!allEmployees && <div>
        <div className='mb2'>
          <span className='medium'>
            <FormattedMessage id='components.employee_selector.Selected Employees:' />
          </span>
          <span className='right'>
            <ActionLink subdued onClick={() => uiState.clearEmployees()}>
              <FormattedMessage id='components.employee_selector.Clear all'/>
            </ActionLink>
          </span>
        </div>
        <div className='clearfix mxn2'>
          {uniqueSelectedEmployees.map(employee => <div key={employee.id} className='col col-6 px2 my1 height-10'>
            <div className='table-cell align-middle'>
              <EmployeeNameWithAvatar employee={employee}/>
            </div>
            <div className='table-cell align-middle pl1'>
              <ActionLink onClick={() => uiState.removeEmployee(employee)}>
                <i className='material-icons h3 align-middle submarine'>{'close'}</i>
              </ActionLink>
            </div>
          </div>)}
        </div>
      </div>}
    </FormLayout>
  );
});

const EmployeeSelector = observer(({uiState, selectors, label, tooltip}) => {
  const {noEmployeesOption} = uiState;

  return (
    <div>
      <LabelHelper label={label} tooltip={tooltip}/>
      <RadioButtonGroup name={uuidv4()}
        onChange={(name, value) => uiState.changeSelectionType(value)}
        selectedValue={uiState.selectionType}>
        <RadioButton className='block my2' value='all_employees'>
          <FormattedMessage id='components.employee_selector.All employees' />
        </RadioButton>
        <RadioButton className='block my2' value='select_employees'>
          <FormattedMessage id='components.employee_selector.Select employees' />
        </RadioButton>
        {noEmployeesOption && <RadioButton className='block my2' value='no_employees'>
          <FormattedMessage id='components.employee_selector.No employees' />
        </RadioButton>}
      </RadioButtonGroup>
      <MultiSelector uiState={uiState} selectors={selectors}/>
    </div>
  );
});

EmployeeSelector.defaultProps = {
  selectors: ['location', 'department', 'employee']
};

export default withState(EmployeeSelector, EmployeeSelectorState, {
  spinner: Spinner
});

// Employee selector allows you to choose segments of employees
// by choosing one of the following categories:
// All or specific employees, departments, locations.

// props:

// - onChange: callback function that will return all selected employees
//
// - selectedEmployees: list of employees who have already been selected
